import classNames from "classnames";
import React from "react";
import { TransitionGroup } from "react-transition-group";

import { asyncTimeout } from "@use/async-timeout";
import { autobind, debounce, throttleToAnimationFrame } from "@use/decorators";
import { preloadImage } from "@use/preload-image";
import { hrtime } from "@use/universal-hrtime";

import { ImageLinkTargets, ImageModel } from "@courses-pm2/api";
import { getIcon } from "@courses-pm2/icons";
import { Ribbon } from "@courses-pm2/ribbon-util-component";

import { ContentFade } from "@courses-pm2/image-component/src/ts/components/transitions/ContentFade";

/**
 * Specifies the minimum margins we'd like to have around the image, based on
 * the size of the viewport and image size (i.e. max size of image will be set
 * to viewport size - MIN_VIEWPORT_MARGIN).
 */
const MIN_VIEWPORT_MARGIN = 100;

/**
 * Specifies the interval in CSS pixels that's used when determining the desired
 * width of the image, so that we don't spam SiteCore with too many resolutions.
 */
const SIZE_INTERVAL = 100;

export type ImageComponentProps = {
  /**
   * The model contains all of the content to render within each instance of
   * the component.
   */
  model: ImageModel;

  /**
   * Specifies if the component is currently being rendered wihtin a lightbox
   * (in which case the handling of certain features should be different).
   */
  inLightbox?: boolean;

  /**
   * An optional callback which will be triggered, if the image should be
   * opened in lightbox mode (ie. on click).
   */
  openLightbox?(args: { model: ImageModel; node: HTMLDivElement }): void;

  /**
   * An optional callback which will be triggered, if the user clicks the
   * image, and an internal link was associated with it.
   */
  openInternalLink?(args: {
    path: string;
    portalId: string;
    newWindow?: boolean;
  }): void;
};

type ImageComponentState = {
  /**
   * Specifies if the image has been successfully loaded (which means that it
   * can safely be rendered within the DOM).
   */
  isLoaded?: boolean;

  /**
   * Specifies if an error has occurred while we tried loading the image (in
   * which case a proper error message should be displayed).
   */
  didFail?: boolean;

  /**
   * Specifies the currently desired width of the image (we're using this
   * piece of information when fetching the image from the remote backend so
   * it's cropped to our specifications).
   */
  desiredWidth?: number;
};

/**
 * Root of the render tree for the standard image-component used to render
 * image elements hosted within PM2.
 */
export class ImageComponent extends React.PureComponent<
  ImageComponentProps,
  ImageComponentState
> {
  /**
   * Setup initial state.
   */
  public state: ImageComponentState = {};

  /**
   * Contains a reference to the wrapper of the component
   */
  protected readonly wrapperRef: React.RefObject<HTMLDivElement | null> =
    React.createRef();

  /**
   * Will contain the naturally desired width of the image (a static value
   * that's going to be inferred as soon as possible after having mounted
   * the image).
   */
  protected naturalWidth: number | undefined = this.props.model.width;

  /**
   * Will contain the naturally desired height of the image (a static value
   * that's going to be inferred as soon as possible after having mounted
   * the image).
   */
  protected naturalHeight: number | undefined = this.props.model.height;

  /**
   * Instantly begin postloading the image when it's mounted into the DOM, and
   * subscribe to all required events.
   */
  public componentDidMount(): void {
    // Instantly calculate the desired width of the image based on the
    // available data...
    const desiredWidth = this.calculateDesiredWidth();

    // Apply desired width instantly so that the component will be properly
    // rendered
    this.setState({ desiredWidth });

    // ... And begin postloading the image in the desired resolution!
    this.postload(desiredWidth).catch(() => {
      /** Fail silently, errors are handled within postload() */
    });

    // Now subscribe required event listeners
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);
  }

  /**
   * Unbind event listeners on unmount
   */
  public componentWillUnmount(): void {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
  }

  /**
   * Primary render method.
   */
  public render(): React.JSX.Element {
    const { model } = this.props;

    // Determine which class names to apply
    const className = classNames("image", {
      "image--contain": model.contain,
    });

    return (
      <div className={className} ref={this.wrapperRef}>
        {this.renderContent()}

        <style jsx>{`
          .image {
            /** Allow usage of position: absolute within */
            position: relative;

            /**
                         * Apply default margin between this and the next
                         * component
                         */
            margin-bottom: 30px;

            /**
                         * Custom styling if image should be contained within
                         * it's parent.
                         */
            &.image--contain {
              /**
                             * Strecth to fill up the available space, if the
                             * image is supposed to be contained within it's
                             * parent.
                             */
              height: 100%;
              width: 100%;
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll inject the actual image within the DOM.
   */
  protected renderContent(): React.JSX.Element | undefined {
    const { model } = this.props;
    const { desiredWidth, didFail, isLoaded } = this.state;

    return (
      <React.Fragment>
        {this.renderTop()}

        <div className="image__wrapper">
          <div
            className="image__aspectRatio1"
            style={{
              maxWidth:
                desiredWidth && !model.contain ? `${desiredWidth}px` : "100%",
            }}
          >
            <div
              className="image__aspectRatio2"
              style={{
                paddingBottom: !model.contain
                  ? `${
                      ((this.naturalHeight || 2) / (this.naturalWidth || 3)) *
                      100
                    }%`
                  : "",
              }}
            />
          </div>

          <TransitionGroup
            component={null /* tslint:disable-line no-null-keyword */}
          >
            {!isLoaded && this.renderPreloader()}
            {isLoaded && !didFail && this.renderImage()}
            {isLoaded && didFail && this.renderError()}
          </TransitionGroup>
        </div>

        {this.renderBottom()}

        <style jsx>{`
          .image__wrapper {
            /** Allow use of position: absolute within */
            position: relative;
          }

          :global(.image--contain) {
            & > .image__aspectRatio1,
            & > .image__aspectRatio2 {
              display: none;
            }

            & > .image__wrapper,
            & > .image__wrapper > :global(.image__aspectRatio) {
              /**
                             * Fill up container if the image is supposed to be
                             * contained within it.
                             */
              width: 100%;
              height: 100%;
            }
          }
        `}</style>
      </React.Fragment>
    );
  }

  /**
   * Rendered that'll display the preloader while image is being fetched
   */
  protected renderPreloader(): React.JSX.Element {
    return (
      <ContentFade key="preloader">
        {(ref) => (
          <>
            <div ref={ref} className="preloader">
              <i className="icon">
                {getIcon("gyldendal", { fillColor: "rgba(0, 0, 0, 0.25)" })}
              </i>
            </div>

            <style jsx>{`
              .preloader::before {
                /** Force rendering of preloader */
                content: "";

                /** Fill entire container */
                display: block;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;

                /* Set a default preloading background color */
                background: rgba(0, 0, 0, 0.1);
              }

              .icon {
                /** Center preload icon within container */
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);

                /** Setup dimensions of icon */
                width: 40px;
                height: 60px;
              }
            `}</style>
          </>
        )}
      </ContentFade>
    );
  }

  /**
   * Renderer that'll inject an error message (a simple exclamation mark) to
   * indicate that we were unable to load the desired image.
   */
  protected renderError(): React.JSX.Element {
    return (
      <ContentFade key="error">
        {(ref) => (
          <>
            <div ref={ref} className="error" />

            <style jsx>{`
              .error {
                /** Fill entire container */
                display: block;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;

                /* Set a default placeholder background color */
                background: rgba(0, 0, 0, 0.1);
              }

              .error::before {
                /** Set the defined icon */
                content: "!";

                /** Place icon in the center of the placeholder */
                display: block;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);

                /** Setup dimensions of error icon */
                box-sizing: border-box;
                height: 64px;
                width: 64px;

                /** Render circle around icon */
                border-radius: 50%;
                border: 2px solid rgba(0, 0, 0, 0.25);

                /** Apply font styling */
                color: rgba(0, 0, 0, 0.25);
                font-size: 45px;
                font-weight: bold;
                line-height: 56px;
                text-align: center;
              }
            `}</style>
          </>
        )}
      </ContentFade>
    );
  }

  /**
   * Renderer that'll inject the actual image inside it's wrapper.
   */
  protected renderImage(): React.JSX.Element | undefined {
    const { model, inLightbox, openLightbox, openInternalLink } = this.props;
    const { desiredWidth } = this.state;

    // Bail out if a desired width is still unknown
    if (desiredWidth === undefined) {
      return undefined;
    }

    // Prepare the source of the image (we're going to request the one in
    // the largest resolution already fetched)
    const imgSrc = `${model.src}?w=${this.getDesiredImageResolution(
      desiredWidth,
    )}`;

    // Determine if the image is clickable, in which case a cursor: pointer
    // should be enabled
    const supportsInternalLink = model.internalLink && openInternalLink;
    const supportsExternalLink = model.externalLink;
    const supportsLightbox =
      model.toolbar.enableLightbox && !inLightbox && openLightbox;
    const supportsClick =
      supportsInternalLink || supportsExternalLink || supportsLightbox;

    const draggable = model.disableDefaultDrag ? false : undefined;
    const preventDefaultDragging = model.disableDefaultDrag
      ? this.preventDefaultDragging
      : undefined;

    const containerClassName = classNames("image__container", {
      "image__container--contained": model.contain,
    });

    const imgClassName = classNames({
      "image__img--supportsClick": supportsClick,
      "image__img--disableDefaultDrag": model.disableDefaultDrag,
    });

    return (
      <ContentFade key="image">
        {(ref) => (
          <>
            <div ref={ref} className={containerClassName}>
              <img
                className={imgClassName}
                src={imgSrc}
                role="presentation"
                alt=""
                draggable={draggable}
                onMouseDown={preventDefaultDragging}
                onTouchStart={preventDefaultDragging}
                onClick={this.onImageClick}
              />
            </div>

            <style jsx>{`
              .image__container {
                /** Overlap aspect ratio wrapper */
                position: absolute;
                top: 0;
                bottom: 0;
                right: 0;
                left: 0;

                /** Render curtains behind the image */
                overflow: hidden;

                &.image__container--contained {
                  background: #383838;
                }
              }

              img {
                /** Make the image extend to fit it's container */
                object-fit: contain;
                height: 100%;
                width: 100%;

                &.image__img--supportsClick {
                  /** Indicate that element is clickable */
                  cursor: pointer;
                }

                &.image__img--disableDefaultDrag {
                  /** Ensure image ghost cannot be dragged if specified. */
                  -ms-user-drag: none;
                  -moz-user-drag: none;
                  -webkit-user-drag: none;
                  touch-action: none;

                  &::selection {
                    background-color: transparent;
                  }
                }
              }
            `}</style>
          </>
        )}
      </ContentFade>
    );
  }

  /**
   * Renderer that'll inject the bar above the image (if one should be
   * visible).
   */
  protected renderTop(): React.JSX.Element | undefined {
    const { model } = this.props;
    const showCaption =
      (model.title || model.description || model.caption) &&
      model.displayCaptionAbove;

    // Bail out if topbar shouldn't be displayed (ie. if there is no
    // ribbon or no caption available)
    if (!model.ribbon && !showCaption) {
      return undefined;
    }

    const className = classNames("image__top", {
      "image__top--withRibbon": !!model.ribbon,
    });

    return (
      <div className={className}>
        {this.renderRibbon()}
        {showCaption && this.renderCaption()}

        <style jsx>{`
          .image__top {
            /** Overlap the actual image */
            position: relative;
            z-index: 2;

            /**
                         * Force container to fit at least a single line of text
                         * within itself
                         */
            min-height: 1.3em;

            /** Apply box styling */
            background: #fff;
            padding: calc(15px - 0.15em) 15px;

            /**
                         * Render shadow on top of the image using pseudo after
                         * element and css gradient.
                         */
            &::after {
              /** Flow right under the topbar */
              position: absolute;
              top: 100%;
              left: 0;
              right: 0;

              /** Apply dropshadow */
              height: 6px;
              background: linear-gradient(
                to bottom,
                rgba(0, 0, 0, 0.06),
                rgba(0, 0, 0, 0)
              );

              /** Force rendering of pseudo element */
              content: "";
            }
          }

          .image__top--withRibbon {
            /**
                         * Ribbons will overlap part of the left side of the bar
                         * so we need to adjust padding accordingly!
                         */
            padding-left: 60px;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll inject the bar below the image (if one should be
   * visible).
   */
  protected renderBottom(): React.JSX.Element | undefined {
    const { model } = this.props;
    const hasCaption = model.title || model.description || model.caption;

    // Bail out if caption shouldn't be displayed below the image
    if (!hasCaption || !model.displayCaptionBelow) {
      return undefined;
    }

    // ... Otherwise render the caption here!
    return (
      <div className="image__bottom">
        {this.renderCaption()}

        <style jsx>{`
          .image__bottom {
            /** Apply box styling */
            padding: calc(15px - 0.15em) 15px;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Helper that'll render the <Ribbon /> utility to inject the icon on the
   * top-left side of the component.
   */
  protected renderRibbon(): React.JSX.Element | undefined {
    if (!this.props.model.ribbon) {
      return undefined;
    }

    return <Ribbon iconLetter={this.props.model.ribbon} />;
  }

  /**
   * Renderer that'll inject the caption associated with the image, either in
   * the top or bottom section.
   */
  protected renderCaption(): React.JSX.Element {
    const { model } = this.props;

    // tslint:disable react-no-dangerous-html
    return (
      <React.Fragment>
        {model.title && (
          <span className="captionElement title">{model.renderTitle()}</span>
        )}
        {model.description && (
          <span className="captionElement description">
            {model.renderDescription()}
          </span>
        )}
        {model.caption && (
          <span className="captionElement caption">
            {model.renderCaption()}
          </span>
        )}

        <style jsx>{`
          .captionElement {
            /** Apply font styling */
            line-height: 1.3em;

            /** Apply custom styling to different elements */
            &.title {
              font-weight: bold;
            }

            &.description,
            &.caption {
              font-style: italic;
            }

            /**
                         * Ensure elements are visually separated by inserting a
                         * space in between each of them.
                         */
            &::after {
              content: " ";
            }
          }
        `}</style>
      </React.Fragment>
    );
  }

  /**
   * Helper that'll calculate the currently desired dimensions of the image,
   * based on it's available space and the configuration of the aspect ratio.
   */
  protected calculateDesiredWidth(): number {
    const { model } = this.props;
    const wrapper = this.wrapperRef.current;

    // Bail out if wrapper isn't available
    if (!wrapper) {
      return window.innerWidth - MIN_VIEWPORT_MARGIN;
    }

    if (this.naturalWidth === undefined || this.naturalHeight === undefined) {
      // If the natural size of the image isn't known yet, then we have no
      // other choice but to assume that the available width is equal to
      // the desired width!
      return !model.enableScaleUp && model.width !== undefined
        ? Math.min(model.width, wrapper.offsetWidth)
        : wrapper.offsetWidth;
    }

    if (this.props.model.contain) {
      // If the image is supposed to be contained within it's parent, then
      // determine the desired width so that it'll be just large enough to
      // fit within it!
      return Math.min(
        wrapper.offsetWidth,
        Math.ceil(
          (wrapper.offsetHeight / this.naturalHeight) * this.naturalWidth,
        ),
      );
    }

    // ... Otherwise we need to calculate the desiredWidth so that the image
    // will utilize the full width of the parent (but still being kept at a
    // height under the maximum allowed size)
    const maxHeight = window.innerHeight - MIN_VIEWPORT_MARGIN;

    const desiredWidth = Math.min(
      wrapper.offsetWidth,
      Math.ceil((maxHeight / this.naturalHeight) * this.naturalWidth),
    );

    // If up-scaling has been disabled, then fall back to the original size
    // of the image - otherwise simply use the calculated desired width
    return !model.enableScaleUp && model.width !== undefined
      ? Math.min(model.width, desiredWidth)
      : desiredWidth;
  }

  /**
   * Helper that'll perform a postloading of the image with the desired width,
   * so it's going to be used.
   */
  protected async postload(desiredWidth: number): Promise<void> {
    try {
      const startedAt = hrtime();

      // Fetch the image with the desired size, so that we can safely
      // store it here...
      const img = await preloadImage(
        `${this.props.model.src}?w=${this.getDesiredImageResolution(
          desiredWidth,
        )}`,
      );

      // Bail out if we were unable to load the image
      if (!img) {
        throw new Error("<ImageComponent />.postload(): Unable to load image.");
      }

      // If the image has just been rendered, then wait at least 650ms
      // before allowing it to become rendered
      if (!this.state.isLoaded && hrtime() > startedAt + 10) {
        await asyncTimeout(Math.max(0, 650 - (hrtime() - startedAt)));
      }

      // Update natural width / height data so that it's always kept up to
      // date
      this.naturalHeight = img.naturalHeight;
      this.naturalWidth = img.naturalWidth;

      // Now that the image has been postloaded, then we can safely update
      // state
      this.setState({
        isLoaded: true,
        didFail: false,
      });
    } catch (err) {
      // Handle errors (making sure we're going to display the fallback)
      this.setState({
        isLoaded: true,
        didFail: true,
      });

      // Log errors on non-production environments
      if (process.env.NODE_ENV !== "production") {
        console.warn(err);
      }
    }
  }

  /**
   * Helper that calculates the desired image resolution based on the current
   * width it's being rendered at.
   */
  protected getDesiredImageResolution(desiredWidth: number): number {
    const pixelRatio = Math.max(1, window.devicePixelRatio * 0.75);
    const desiredImageResolution =
      Math.ceil((desiredWidth * pixelRatio) / SIZE_INTERVAL) * SIZE_INTERVAL;

    return this.props.model.width
      ? Math.min(this.props.model.width, desiredImageResolution)
      : desiredImageResolution;
  }

  /**
   * Listener that'll prevent Firefox from starting default dragging of images
   * if that feature has been explicitly disabled.
   */
  protected preventDefaultDragging = (
    evt: React.MouseEvent | React.TouchEvent,
  ) => {
    evt.preventDefault();
  };

  /**
   * Listener that's triggered when the user clicks on the image, prompting
   * for it to be opened in a lightbox.
   */
  protected onImageClick = () => {
    const { model, inLightbox, openLightbox, openInternalLink } = this.props;

    // If an internal link is associated with the image, and a callback has
    // been associated with the component to handle that kind of links, then
    // do it now
    if (model.internalLink && openInternalLink) {
      openInternalLink({
        path: model.internalLink,
        portalId: model.portalId,
        newWindow: model.target === ImageLinkTargets.NEW_WINDOW,
      });

      // Abort further execution, we don't want to have multiple actions
      // on click!
      return;
    }

    // If an external link is associated with the image, then open the link
    // in a new window right away
    if (model.externalLink) {
      window.open(model.externalLink, "_blank");

      // Abort further execution, we don't want to have multiple actions
      // on click!
      return;
    }

    // If the image supports lightbox opening, then request that it's being
    // done right away
    if (
      !inLightbox &&
      openLightbox &&
      model.toolbar.enableLightbox &&
      this.wrapperRef.current
    ) {
      openLightbox({
        model,
        node: this.wrapperRef.current,
      });
    }
  };

  /**
   * Listener that'll be executed every time the viewport changes dimensions,
   * ensuring that the image is kept within bounds, and that an image of
   * proper dimensions have been loaded.
   */
  @autobind()
  @throttleToAnimationFrame()
  protected onResize(): void {
    // Instantly calculate the desired width of the image based on the
    // available data...
    const desiredWidth = this.calculateDesiredWidth();

    // Apply desired width instantly so that the component will be properly
    // rendered
    this.setState({ desiredWidth });

    // At the end of it all, attempt to postload the desired image
    this.postloadOnResize(desiredWidth).catch(() => {
      /** Fail silently, errors are handled within postload() */
    });
  }

  /**
   * Debounced helper that's triggered during resize, making sure that an
   * updated image will be loaded if user has made his window larger.
   */
  @debounce(200)
  protected async postloadOnResize(desiredWidth: number): Promise<void> {
    if (
      this.state.desiredWidth === undefined ||
      desiredWidth > this.state.desiredWidth
    ) {
      this.postload(desiredWidth).catch(() => {
        /** Fail silently, errors are handled within postload() */
      });
    }
  }
}
